// src/pages/invoices/modals/RecurringInvoiceModal.js
import React, { useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import SearchBuildingBar from "../../../components/SearchBuildingBar";
import { useAuth } from "../../../providers/authprovider";
import handleResponseCode from "../utils/handleResponseCode";
import {
  FREQUENCIES,
  formatDate,
  lastDayFromMonthInput,
  nextInvoiceDate,
  toMonthInput,
  todayStr,
} from "../utils/recurringDates";

export default function RecurringInvoiceModal({
  show,
  onHide,
  // edit mode: { id, building, contract_number, frequency, amount, next_invoice_date, description }
  recurringInvoice = null,
  onSaved, // (recurringInvoice) => void
}) {
  const { invoices } = useAuth();
  const isEdit = !!recurringInvoice?.id;

  const [building, setBuilding] = useState(null);
  const [frequency, setFrequency] = useState("MONTHLY");
  const [amount, setAmount] = useState("");
  const [month, setMonth] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // reset przy każdym otwarciu
  React.useEffect(() => {
    if (!show) return;

    setBuilding(recurringInvoice?.building || null);
    setFrequency(recurringInvoice?.frequency || "MONTHLY");
    setAmount(
      recurringInvoice?.amount !== undefined && recurringInvoice?.amount !== null
        ? String(recurringInvoice.amount)
        : "",
    );
    setMonth(toMonthInput(recurringInvoice?.next_invoice_date));
    setDescription(recurringInvoice?.description || "");
    setSaving(false);
    setError("");
  }, [show, recurringInvoice?.id]);

  // numer umowy pochodzi z obiektu (buildings.folder_num, zwracany w /buildings/shortinfo)
  const contractNumber =
    building?.folder_num ??
    (building?.id === recurringInvoice?.building?.id
      ? recurringInvoice?.contract_number
      : "");

  const invoiceDate = lastDayFromMonthInput(month);
  // terminy, które już minęły, traktujemy jako wystawione -> zapisujemy pierwszą przyszłą datę
  const today = todayStr();
  const firstAlreadyIssued = !isEdit && !!invoiceDate && invoiceDate < today;
  let savedNextDate = invoiceDate;
  while (firstAlreadyIssued && savedNextDate && savedNextDate < today) {
    savedNextDate = nextInvoiceDate(savedNextDate, frequency);
  }
  const parsedAmount = Number(String(amount).replace(",", "."));

  const validate = () => {
    if (!building?.id) return "Wybierz obiekt.";
    if (!frequency) return "Wybierz częstotliwość.";
    if (amount === "" || Number.isNaN(parsedAmount) || parsedAmount <= 0)
      return "Wpisz poprawną kwotę.";
    if (!invoiceDate)
      return isEdit
        ? "Wybierz miesiąc kolejnej faktury."
        : "Wybierz miesiąc pierwszej faktury.";
    return "";
  };

  const handleClose = () => {
    if (saving) return;
    onHide?.();
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSaving(true);
    setError("");

    const data = {
      buildingId: building.id,
      frequency,
      amount: Math.round(parsedAmount * 100) / 100,
      nextInvoiceDate: savedNextDate,
      description,
    };

    const result = isEdit
      ? await invoices.updateRecurringInvoice(recurringInvoice.id, data)
      : await invoices.addRecurringInvoice(data);

    if (!result?.success) {
      setError(handleResponseCode(result?.status, result?.error));
      setSaving(false);
      return;
    }

    onSaved?.(result.content);
    onHide?.();
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          {isEdit ? "Edytuj fakturę cykliczną" : "Nowa faktura cykliczna"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Obiekt</Form.Label>
            <SearchBuildingBar
              onSelect={(b) => setBuilding(b || null)}
              defaultValue={building}
              enabled={!saving}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Numer umowy</Form.Label>
            <Form.Control
              type="text"
              value={contractNumber || ""}
              placeholder={building ? "Brak numeru umowy w obiekcie" : "Uzupełni się po wybraniu obiektu"}
              readOnly
              disabled
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Częstotliwość</Form.Label>
                <Form.Select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  disabled={saving}
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Kwota (zł)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0,00"
                  disabled={saving}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>
              {isEdit ? "Termin kolejnej faktury" : "Termin pierwszej faktury"}
            </Form.Label>
            <Form.Control
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              disabled={saving}
            />
            {invoiceDate ? (
              <div className="mt-1 small text-muted">
                Faktura na ostatni dzień miesiąca:{" "}
                <strong>{formatDate(invoiceDate)}</strong>
              </div>
            ) : null}
            {firstAlreadyIssued && savedNextDate ? (
              <div className="mt-1 small text-muted">
                Ten termin już minął — faktury do dziś zostaną potraktowane jako
                wystawione. Data kolejnej faktury:{" "}
                <strong>{formatDate(savedNextDate)}</strong>
              </div>
            ) : null}
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Opis</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Dane potrzebne do wystawienia faktury..."
              disabled={saving}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Anuluj
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Zapisywanie...
            </>
          ) : (
            "Zapisz"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
